/**
 * Shared design tokens + content for the Donate page redesign (CheckoutV2).
 * Kept separate so the new components don't depend on the legacy Checkout
 * constants and the rest of the app stays untouched.
 */

import moment from "moment";
import { TFunction } from "i18next";
import { DEFAULT_DATE_FORMAT } from "Utils/Constants";
import { User } from "../../../../types/Auth";
import { DonationGift } from "../../../../types/Donate";

/** Brand purple used throughout the redesign. */
export const PURPLE = "#7C6FE0";
/** Lighter purple used for borders/accents. */
export const PURPLE_LIGHT = "#8B7FEA";
/** Near-black page/section background behind the cards. */
export const SECTION_BG = "#333333";
/** Dark surface used by the summary + gift cards. */
export const CARD_DARK = "#16162A";
/** Off-white surface used by the donation form card. */
export const FORM_BG = "#FAF8F5";

export const FONT_SANS = `"Inter", sans-serif`;

export type DonationFrequency = "one-time" | "monthly";

export type PaymentMethod = "card" | "paypal" | "checking";

/** Preset one-time donation amounts (US dollars), laid out 4-up. */
export const ONE_TIME_AMOUNTS = [60, 75, 100, 150, 250, 500, 1000, 10000];

/** Preset monthly donation amounts (US dollars). */
export const MONTHLY_AMOUNTS = [10, 15, 20, 25, 50, 100];

/** Preset amounts for the given donation frequency. */
export const getAmountsForFrequency = (frequency: DonationFrequency) =>
  frequency === "monthly" ? MONTHLY_AMOUNTS : ONE_TIME_AMOUNTS;

/** Amount pre-selected on load and when switching to each frequency. */
export const ONE_TIME_DEFAULT_AMOUNT = 100;
export const MONTHLY_DEFAULT_AMOUNT = 20;
export const getDefaultAmount = (frequency: DonationFrequency) =>
  frequency === "monthly" ? MONTHLY_DEFAULT_AMOUNT : ONE_TIME_DEFAULT_AMOUNT;

/** Amount that unlocks the Annual Membership tier / thank-you gifts. */
export const ANNUAL_MEMBERSHIP_AMOUNT = 150;

/**
 * Contextual membership label shown in the hover tooltip for a donation amount
 * (and the custom "Other Amount" field):
 *  - below $100 → "{n} month membership" in $10 increments ($50 → 5 months)
 *  - $100–$149  → "Save 17% Annual Membership"
 *  - $150–$249  → "Annual Membership"
 *  - $250+      → "Patron"
 * Returns "" for empty/zero amounts so no tooltip is shown.
 */
export const getMembershipTooltip = (
  amount: number,
  t: TFunction
): string => {
  if (!amount || amount <= 0) return "";
  if (amount < 100)
    return t("donationPage.monthMembershipTooltip", {
      count: Math.max(1, Math.floor(amount / 10)),
    });
  if (amount < 150) return t("donationPage.saveAnnualMembership");
  if (amount < 250) return t("donationPage.annualMembership");
  return t("donationPage.patron");
};

/** Minimum donation required to choose a thank-you gift. */
export const GIFT_MINIMUM = 100;

/**
 * Smallest donation accepted through the "Other Amount" field, matching the
 * legacy checkout's DonationAmountStep (`donationAmount < 10_00` → error). It
 * applies only to a typed amount — the preset buttons all sit above it.
 */
export const MIN_DONATION_AMOUNT = 10;

/** Free-trial length in days. Must match FREE_TRIAL_DAYS in JazzGrooveCheckoutV2,
 *  which computes the real trial_end when the subscription is created. */
export const TRIAL_DAYS = 14;

/**
 * Donation Summary tier thresholds (one-time donations):
 *  - below $100        → "One-Time Donation (X Months)"
 *  - $100–$199         → "One-Time Donation, Annual Membership"
 *  - $200+             → "One-Time Donation, Patron"
 */
export const ANNUAL_MEMBERSHIP_MIN_AMOUNT = 100;
export const PATRON_MIN_AMOUNT = 200;

/** Membership months a sub-$100 one-time donation buys ($10 each). */
export const getMembershipMonths = (amount: number) =>
  Math.max(1, Math.floor(amount / 10));

/** Annual memberships a Patron ($200+) donation can gift (~$100 each). */
export const getGiftableMemberships = (amount: number) =>
  Math.max(1, Math.floor(amount / 100));

/**
 * Transaction fee a donor can opt to cover so 100% of their gift reaches the
 * station — 2.5% of the selected donation amount (e.g. $1.50 on $60).
 */
export const FEE_RATE = 0.025;
export const calcCoverFee = (amount: number) =>
  Math.round(amount * FEE_RATE * 100) / 100;

/**
 * The auto-renew option only applies to one-time donations of at least this
 * amount — smaller gifts don't qualify for annual membership tiers, and
 * monthly donations are already recurring.
 */
export const AUTO_RENEW_MIN_AMOUNT = 100;
export const shouldShowAutoRenew = (
  frequency: DonationFrequency,
  amount: number
) => frequency === "one-time" && amount >= AUTO_RENEW_MIN_AMOUNT;

export interface GiftSlide {
  /** Image served from `public/DonatePage/`. */
  image: string;
  /** Caption shown in the purple footer bar (English fallback). */
  title: string;
  /** i18n key for the caption; translated at render. */
  titleKey: string;
}

const asset = (file: string) => `${process.env.PUBLIC_URL}/DonatePage/${file}`;

export const GIFT_SLIDES: GiftSlide[] = [
  { image: asset("Rectangle29.png"), title: "Jazz Artist Poster", titleKey: "donationPage.giftPosterCaption" },
  { image: asset("Rectangle33.png"), title: "Jazz Groove Cup", titleKey: "donationPage.giftCupCaption" },
  { image: asset("magazine.png"), title: "The Jazz Groove Magazine", titleKey: "donationPage.giftMagazineCaption" },
];

/** Sizes offered when the donor chooses the apparel (T-shirt) thank-you gift. */
export const SHIRT_SIZES = ["Small", "Medium", "Large", "Extra Large"];

/**
 * DISPLAY-ONLY translation for gift sizes and names.
 *
 * The underlying strings are Chargebee data: a chosen size has to match the
 * addon variant's `name` exactly for us to resolve its id, and gift names come
 * from the `/donate/gifts` catalog. So the stored VALUE always stays as Chargebee
 * supplies it — only the label the donor reads is translated. Anything not in
 * these maps (a new catalog item, an unusual variant) falls through unchanged,
 * so the UI degrades to English rather than breaking.
 */
const SIZE_LABEL_KEYS: Record<string, string> = {
  "small": "donationPage.sizeSmall",
  "s": "donationPage.sizeSmall",
  "medium": "donationPage.sizeMedium",
  "m": "donationPage.sizeMedium",
  "large": "donationPage.sizeLarge",
  "l": "donationPage.sizeLarge",
  "extra large": "donationPage.sizeExtraLarge",
  "x-large": "donationPage.sizeExtraLarge",
  "xl": "donationPage.sizeExtraLarge",
};

const GIFT_LABEL_KEYS: Record<string, string> = {
  "jazz groove coffee mug (14 oz)": "donationPage.giftCoffeeMug",
  "jazz groove magnet": "donationPage.giftMagnet",
  "jazz groove men's t-shirt": "donationPage.giftMensTshirt",
  "jazz groove women's t-shirt": "donationPage.giftWomensTshirt",
  "jazz groove snapback baseball cap": "donationPage.giftSnapbackCap",
  "jazz groove sticker": "donationPage.giftSticker",
  "jazz groove tote bag (vinyl)": "donationPage.giftToteVinyl",
  "jazz groove tote bag (cloth)": "donationPage.giftToteCloth",
  'miles davis - "kind of blue" cd': "donationPage.giftKindOfBlueCd",
  "the miles davis quintet poster": "donationPage.giftQuintetPoster",
  "ken burns, “jazz” 10-dvd set": "donationPage.giftKenBurnsDvd",
  "miles davis twenty albums": "donationPage.giftMilesTwentyAlbums",
  "jazz artist poster": "donationPage.giftArtistPoster",
  "jazz groove cup": "donationPage.giftCup",
  "jazz groove t-shirt": "donationPage.giftTshirt",
};

const labelFor = (
  value: string,
  map: Record<string, string>,
  t: TFunction
): string => {
  if (!value) return value;
  const key = map[value.trim().toLowerCase()];
  return key ? t(key) : value;
};

/** Translated label for a gift size; the value itself is unchanged. */
export const sizeLabel = (size: string, t: TFunction) =>
  labelFor(size, SIZE_LABEL_KEYS, t);

/** Translated label for a gift name; the value itself is unchanged. */
export const giftLabel = (name: string, t: TFunction) =>
  labelFor(name, GIFT_LABEL_KEYS, t);

/** Whether a gift requires the donor to pick a size (apparel/T-shirts). */
export const giftNeedsSize = (giftTitle: string): boolean =>
  giftTitle.toLowerCase().includes("shirt");

/**
 * Static thank-you gift list used as a fallback when the `/donate/gifts`
 * endpoint is unavailable (e.g. local testing without a valid API key), so the
 * gift dropdown still shows options instead of rendering empty. The live API
 * response always takes precedence whenever it returns gifts. Only the fields
 * the modal actually reads (`id`, `name`, `meta_data.variants[].name`) are
 * populated, hence the cast.
 */
export const FALLBACK_GIFTS = [
  { id: "fallback-poster", name: "Jazz Artist Poster" },
  { id: "fallback-cup", name: "Jazz Groove Cup" },
  {
    id: "fallback-tshirt",
    name: "Jazz Groove T-shirt",
    meta_data: {
      variants: SHIRT_SIZES.map((size) => ({
        id: `fallback-tshirt-${size}`,
        name: size,
      })),
    },
  },
] as unknown as DonationGift[];

/**
 * Which top-of-form block to render in place of "Your Information", derived from
 * the donor's Auth0 state in {@link DonateSection}:
 *  - "new"     → anonymous, first-time visitor (email + "create account" copy).
 *  - "sign-in" → recognized returning visitor who isn't signed in yet
 *                (email + password + "Sign in" → Auth0 login).
 *  - "member"  → signed-in donor; greeting + membership status (e.g. the
 *                "Inactive Membership" message when their plan has lapsed).
 *  - "complete-registration" → paid before but never created a login (has a
 *                DynamoDB record, no Auth0 account) → finish setting up.
 */
export type DonorVariant =
  | "new"
  | "sign-in"
  | "member"
  | "complete-registration";

/**
 * Whether a signed-in donor's membership has lapsed: they either never held a
 * plan, or their current term has already ended. Mirrors the "free/inactive
 * plan" treatment the header uses (no plan ⇒ inactive).
 */
export const isMembershipInactive = (user: User): boolean => {
  if (!user?.plan) return true;
  return !!user.ttl && moment.unix(user.ttl).isBefore(moment());
};

/**
 * The donor's membership end date formatted for display (e.g. "Jun 18, 2026"),
 * or null when unknown. `ttl` is the term end as seconds since the UNIX epoch.
 */
export const getMembershipEndDate = (user: User): string | null =>
  user?.ttl ? moment.unix(user.ttl).format(DEFAULT_DATE_FORMAT) : null;
